/* ioc-shop.js · Run 5: the Build a Box builder + the cart's six-cookie gate.

   Builder modes (data-mode on [data-ioc-builder], decided by Liquid from theme settings):
     box      → picks live in sessionStorage until the box is full, then ONE native Horizon product form adds the
                Box variant for the chosen size (+ optional cadence = selling plan) with line-item properties
                Flavors and _Premium count (underscore: hidden from the shopper, read by the cart). Prices come pre-formatted from Liquid in the JSON block; this file only
                chooses which variant's numbers to show.
     fallback → no Box product yet: the grid's native add-to-cart adds single cookies; slots paint from /cart.js.
     legacy   → singles model, same as fallback painting (Build a Dozen as it was).

   B4 (SA port, row 4) motivation layer, shared by box + fallback: crumb tray, remaining-count state line, sticky
   summary bar armed by the first pick and a 40px scroll, roving arrow keys on the size radios, trimTo() when the size
   drops (largest stack first, never clears the box), and a polite toast. Ported from sa-build-a-box-packs.liquid
   without any of its price maths (RATE / price() / packTotal() stay at SA; IOC prices come from the variant).

   Cart gate (box model): Liquid prints [data-ioc-cart-gate="blocked"] inside the cart when fewer than the minimum
   single cookies are in it and no Box line exists. This file disables the checkout button in that cart container
   and hides the dynamic checkout buttons while blocked. Client-side only: server-side enforcement needs a Shopify
   Function (cart validation), which is not built. */
(function () {
  function esc(s) { return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;"); }
  function fmt(tpl, map) { return String(tpl || "").replace(/\{(\w+)\}|\[(\w+)\]/g, function (m, a, b) { var k = a || b; return map[k] != null ? map[k] : m; }); }
  function cartJson() { return fetch("/cart.js", { headers: { Accept: "application/json" } }).then(function (r) { return r.json(); }); }
  function pad(i) { return String(i).padStart(2, "0"); }
  function thumb(url) { return url ? url.replace(/(\.[a-z]+)(\?|$)/, "_200x$1$2") : ""; }
  /* A2: the headline price must equal what /cart/add.js actually charges once a premium surcharge is atomically
     added, so the total (variant + surcharge) has to be computed and formatted client-side. Ported (not imported,
     this file is a classic script) from assets/money-formatting.js's replication of the `money` Liquid filter, cut
     down to the placeholders Shopify actually emits in a shop's money_format. */
  var ZERO_DECIMAL = { BIF: 1, CLP: 1, DJF: 1, GNF: 1, ISK: 1, JPY: 1, KMF: 1, KRW: 1, PYG: 1, RWF: 1, UGX: 1, VND: 1, VUV: 1, XAF: 1, XOF: 1, XPF: 1 };
  function fmtMoney(cents, tpl, cur) {
    var basePrecision = ZERO_DECIMAL[cur] ? 0 : 2;
    var divisor = Math.pow(10, basePrecision);
    function group(s, sep) { return s.replace(/\B(?=(\d{3})+(?!\d))/g, sep); }
    function render(placeholder) {
      var p = basePrecision, thou = ",", dec = ".";
      if (placeholder === "amount_no_decimals") p = 0;
      else if (placeholder === "amount_with_comma_separator") { thou = "."; dec = ","; }
      else if (placeholder === "amount_no_decimals_with_comma_separator") { thou = "."; p = 0; }
      else if (placeholder === "amount_no_decimals_with_space_separator") { thou = " "; p = 0; }
      else if (placeholder === "amount_with_space_separator") { thou = " "; dec = ","; }
      else if (placeholder === "amount_with_apostrophe_separator") { thou = "'"; }
      var n = (Number(cents || 0) / divisor).toFixed(Math.max(p, 0));
      var parts = n.split(".");
      parts[0] = group(parts[0], thou);
      return p <= 0 ? parts[0] : parts[0] + dec + (parts[1] || "").padEnd(p, "0");
    }
    return String(tpl || "${{amount}}").replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, ph) { return ph === "currency" ? cur : render(ph); });
  }
  function afterCartEvent(e) { var p = (e.detail && e.detail.promise) || e.promise; return p && typeof p.then === "function" ? p : Promise.resolve(); }
  function motionOff() {
    if (document.documentElement.classList.contains("ioc-motion-paused")) return true;
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }

  var trustTracks = new WeakSet();
  function initTrustPanels(container) {
    container.querySelectorAll("[data-ioc-trust-track]").forEach(function (track) {
      if (trustTracks.has(track)) return;
      trustTracks.add(track);
      var panel = track.closest(".box-trust");
      var prev = panel.querySelector("[data-ioc-trust-prev]");
      var next = panel.querySelector("[data-ioc-trust-next]");
      var counter = panel.querySelector("[data-ioc-trust-count]");
      var total = track.children.length;
      var frame = null;
      var drag = null;

      function update() {
        frame = null;
        if (!track.clientWidth) return;
        var index = Math.max(0, Math.min(total - 1, Math.round(track.scrollLeft / track.clientWidth)));
        var label = (index + 1) + " / " + total;
        if (counter.textContent !== label) counter.textContent = label;
        if (prev.disabled !== (index === 0)) prev.disabled = index === 0;
        if (next.disabled !== (index === total - 1)) next.disabled = index === total - 1;
      }
      function scheduleUpdate() {
        if (frame === null) frame = requestAnimationFrame(update);
      }
      function step(direction) {
        track.scrollBy({ left: direction * track.clientWidth, behavior: motionOff() ? "auto" : "smooth" });
      }
      prev.addEventListener("click", function () { step(-1); });
      next.addEventListener("click", function () { step(1); });
      track.addEventListener("keydown", function (event) {
        if (event.target !== track || (event.key !== "ArrowRight" && event.key !== "ArrowLeft")) return;
        event.preventDefault();
        step(event.key === "ArrowRight" ? 1 : -1);
      });
      track.addEventListener("scroll", scheduleUpdate, { passive: true });
      track.addEventListener("pointerdown", function (event) {
        if (event.pointerType !== "mouse" || event.button !== 0) return;
        drag = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, scrollLeft: track.scrollLeft, active: false };
      });
      track.addEventListener("pointermove", function (event) {
        if (!drag || event.pointerId !== drag.pointerId) return;
        var distance = event.clientX - drag.startX;
        if (!drag.active) {
          if (Math.abs(event.clientY - drag.startY) > Math.abs(distance)) { drag = null; return; }
          if (Math.abs(distance) < 6) return;
          drag.active = true;
          track.classList.add("is-dragging");
          track.setPointerCapture(event.pointerId);
        }
        event.preventDefault();
        track.scrollLeft = drag.scrollLeft - distance;
      });
      function endDrag() {
        if (!drag) return;
        var active = drag.active;
        var pointerId = drag.pointerId;
        drag = null;
        if (!active) return;
        var width = track.clientWidth;
        var index = width ? Math.round(track.scrollLeft / width) : 0;
        track.classList.remove("is-dragging");
        if (track.hasPointerCapture(pointerId)) track.releasePointerCapture(pointerId);
        track.scrollTo({ left: index * width, behavior: motionOff() ? "auto" : "smooth" });
      }
      track.addEventListener("pointerup", endDrag);
      track.addEventListener("pointercancel", endDrag);
      track.addEventListener("lostpointercapture", endDrag);
      track.addEventListener("pointerleave", function () { if (drag && !drag.active) drag = null; });
      var resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(track);
      function unload(event) {
        if (!event.target.contains(track)) return;
        resizeObserver.disconnect();
        if (frame !== null) cancelAnimationFrame(frame);
        document.removeEventListener("shopify:section:unload", unload);
      }
      document.addEventListener("shopify:section:unload", unload);
      update();
    });
  }
  ready(function () { initTrustPanels(document); });
  document.addEventListener("shopify:section:load", function (event) { initTrustPanels(event.target); });

  /* ===================================== B4 row 6: cart engine (SA sa-cart.js port) ==== */
  /* cartSectionIds(): every Section Rendering id the cart UIs listen for, so the drawer, the ship meter, the gate and
     the header bubble all repaint from ONE /cart/add.js response. */
  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll("cart-items-component[data-section-id], cart-icon[data-section-id]").forEach(function (el) {
      var id = el.getAttribute("data-section-id");
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }
  /* postAddAndAnnounce(): one POST with an items array, the deferred shopify:cart:lines-update Horizon's own drawer,
     cart items and bubble listen for (dispatched on `target` so the drawer's modal check and the builder's own
     listener see the right origin), the /cart.js re-read, and a polite announcement. No window global. */
  /* @shopify/events for this classic script. Final-B: Shopify's CDN minifier rewrites a dynamic import() in a classic
     script into require(), which does not exist in a browser, so on the served theme the module always came back null,
     no CartLinesUpdateEvent was sent and the builder fell back to reloading the page instead of opening the drawer.
     An inline module script is never minified and resolves the specifier through the theme's import map, so it loads
     the module on every host. Shared with assets/ioc-cro.js through window.__iocEventsMod, so it is fetched once. */
  function eventsModule() {
    if (window.__iocEventsMod) return window.__iocEventsMod;
    window.__iocEventsMod = new Promise(function (resolve) {
      var done = false;
      window.__iocEventsDone = function (m) { if (done) return; done = true; resolve(m || null); };
      var s = document.createElement("script");
      s.type = "module";
      s.textContent = 'import("@shopify/events").then(function (m) { window.__iocEventsDone(m); }, function () { window.__iocEventsDone(null); });';
      document.head.appendChild(s);
      setTimeout(function () { window.__iocEventsDone(null); }, 8000);
    });
    return window.__iocEventsMod;
  }
  function postAddAndAnnounce(target, items, meta) {
    var itemCount = items.reduce(function (a, i) { return a + (i.quantity || 1); }, 0);
    var lines = items.map(function (i) { return { merchandiseId: String(i.id), quantity: i.quantity || 1 }; });
    return eventsModule().then(function (ev) {
      var Ev = ev && ev.CartLinesUpdateEvent;
      var deferred = Ev ? Ev.createPromise() : null;
      if (Ev) target.dispatchEvent(new Ev({ action: "add", context: "product", lines: lines, promise: deferred.promise }));
      var body = { items: items };
      var secs = cartSectionIds();
      if (secs.length) body.sections = secs.join(",");
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      return fetch(root + "cart/add.js", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(body) })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
        .then(function (res) {
          var j = res.json || {};
          var failed = !res.ok || j.status;
          return cartJson().then(function (ajaxCart) {
            var detail = { items: ajaxCart.items, source: meta.source || "ioc", sourceId: String(meta.sourceId || ""), itemCount: itemCount, productId: meta.productId, sections: failed ? undefined : j.sections, didError: !!failed };
            if (deferred) deferred.resolve({ cart: Ev.createCartFromAjaxResponse(ajaxCart), detail: detail });
            if (!failed) announce(ajaxCart, itemCount);
            return { didError: !!failed, message: failed ? (j.description || j.message || "We could not add that to your cart.") : "", cart: ajaxCart };
          });
        })
        .catch(function (err) { if (deferred) deferred.reject(err); throw err; });
    });
  }
  /* One polite live region for every add, whoever made it (builder, PDP, collection card, Duck Pick). */
  var liveEl = null;
  function liveRegion() {
    if (liveEl) return liveEl;
    liveEl = document.createElement("div");
    liveEl.id = "ioc-cart-live";
    liveEl.className = "visually-hidden";
    liveEl.setAttribute("role", "status");
    liveEl.setAttribute("aria-live", "polite");
    liveEl.setAttribute("aria-atomic", "true");
    document.body.appendChild(liveEl);
    return liveEl;
  }
  var liveTimer;
  function announce(cart, added) {
    var el = liveRegion();
    var n = cart && typeof cart.item_count === "number" ? cart.item_count : null;
    el.textContent = "";
    setTimeout(function () {
      el.textContent = (added ? "Added to cart. " : "Cart updated. ") + (n !== null ? n + (n === 1 ? " item" : " items") + " in your cart." : "");
    }, 50);
    clearTimeout(liveTimer);
    liveTimer = setTimeout(function () { el.textContent = ""; }, 6000);
  }
  document.addEventListener("shopify:cart:lines-update", function (e) {
    var d = e.detail || {};
    if (d.source === "ioc-box" || d.source === "ioc-pdp") return; /* announced by postAddAndAnnounce */
    afterCartEvent(e).then(function (res) {
      var det = res && res.detail;
      if (!det || det.didError) return;
      var c = res.cart || {};
      var count = typeof c.totalQuantity === "number" ? c.totalQuantity : (typeof c.item_count === "number" ? c.item_count : null);
      announce({ item_count: count }, e.action === "add" || (det.itemCount || 0) > 0);
    }, function () {});
  });

  /* Final-B: a product page gift note with a line break. Horizon's product form posts multipart FormData, which sends
     every break as CRLF (so a 240 note arrives longer than the counter said). When the note has a break, the same add
     goes out as JSON instead, where a break stays one \n: same variant, quantity, plan and properties, one POST. A note
     with no break keeps Horizon's own path untouched. */
  var pdpBusy = false;
  document.addEventListener("click", function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('form[data-type="add-to-cart-form"] [name="add"]') : null;
    if (!btn || btn.disabled || btn.hasAttribute("data-ioc-box-add") || btn.closest("[data-ioc-builder]")) return;
    var form = btn.form || btn.closest("form");
    if (!form || !form.checkValidity()) return;
    var fd = new FormData(form);
    if (!/[\r\n]/.test(String(fd.get("properties[Gift note]") || ""))) return;
    e.preventDefault();
    if (pdpBusy) return;
    pdpBusy = true;
    var props = {};
    fd.forEach(function (v, k) { var m = /^properties\[(.+)\]$/.exec(k); if (m && String(v).trim() !== "") props[m[1]] = String(v).replace(/\r\n?/g, "\n"); });
    var item = { id: Number(fd.get("id")), quantity: Math.max(1, Number(fd.get("quantity")) || 1), properties: props };
    if (fd.get("selling_plan")) item.selling_plan = Number(fd.get("selling_plan"));
    var host = btn.closest("product-form-component") || form;
    btn.disabled = true;
    postAddAndAnnounce(host, [item], { source: "ioc-pdp", sourceId: host.id || "", productId: host.getAttribute("data-product-id") || "" })
      .then(function (res) {
        var err = host.querySelector("[ref='addToCartTextError']");
        if (!err) return;
        var t = err.querySelector("[data-ioc-err]");
        if (!t) { t = document.createElement("span"); t.setAttribute("data-ioc-err", ""); err.appendChild(t); }
        t.textContent = res && res.didError ? " " + res.message : "";
        err.classList.toggle("hidden", !(res && res.didError));
      }, function () {})
      .then(function () { pdpBusy = false; btn.disabled = false; });
  }, true);

  /* =========================================================== builder ==== */
  /* CRO rebuild 2026-09-23 (B-Builder, CRO-SPEC 4.3). Box mode is phone-first: an in-card + Add that becomes a
     [-] n [+] stepper, a BOX-DOCK on screen from load, a "Your box" sheet, a non-destructive gluten switch, and the
     URL contract every other page links with:
       ?tier=6|12|18|24   size
       ?plan=month        pre-selects that plan (only because the shopper chose it upstream, e.g. /pages/subscribe; D9)
       ?add=<handle>      seeds a flavor; repeat it or comma-separate for several
       ?gift=1            opens the gift note
     sessionStorage "ioc-box" = { tier, type, plan, planName, picks[], gift }.
     Money path unchanged: ONE native product form for the Box variant, or, when a premium flavor is in the box, ONE
     /cart/add.js POST whose items[] carries the Box line AND the surcharge line (atomicItems). Never two adds. */
  var root = document.querySelector("[data-ioc-builder]");
  if (root) initBuilder(root);

  function initBuilder(root) {
    var mode = root.getAttribute("data-mode") || "legacy";
    var slots = root.querySelector("#iocSlots");
    var status = root.querySelector("#iocBoxStatus");
    var tierBtns = Array.prototype.slice.call(root.querySelectorAll("[data-ioc-tier]"));
    var max = Number(root.getAttribute("data-tier") || (slots && slots.getAttribute("data-slots")) || 12);
    var params = new URLSearchParams(window.location.search);
    var noteEl = root.querySelector("[data-ioc-note]");

    var noteTimer;
    function note(m) {
      if (!noteEl || !m) return;
      noteEl.textContent = "";
      noteEl.classList.remove("show");
      void noteEl.offsetWidth;
      noteEl.textContent = m;
      noteEl.classList.add("show");
      clearTimeout(noteTimer);
      noteTimer = setTimeout(function () { noteEl.classList.remove("show"); }, 2800);
    }

    /* size radios: aria-checked + roving tabindex follow the selection; arrows move and select */
    function setTier(n) {
      max = n;
      if (slots) slots.setAttribute("data-slots", String(n));
      tierBtns.forEach(function (b) {
        var on = Number(b.getAttribute("data-ioc-tier")) === n;
        b.classList.toggle("is-on", on);
        b.setAttribute("aria-checked", on ? "true" : "false");
        b.setAttribute("tabindex", on ? "0" : "-1");
      });
    }
    root.addEventListener("keydown", function (e) {
      var t = e.target.closest ? e.target.closest("[data-ioc-tier][role='radio']") : null;
      if (!t) return;
      var keys = ["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp", "Home", "End"];
      if (keys.indexOf(e.key) === -1) return;
      e.preventDefault();
      var idx = tierBtns.indexOf(t), nx;
      if (e.key === "Home") nx = 0;
      else if (e.key === "End") nx = tierBtns.length - 1;
      else nx = (e.key === "ArrowRight" || e.key === "ArrowDown") ? (idx + 1) % tierBtns.length : (idx - 1 + tierBtns.length) % tierBtns.length;
      tierBtns[nx].focus();
      tierBtns[nx].click();
    });
    /* Final-B: the delivery choice (one-time or a plan) is a radio group too, painted in up to three places (desktop
       summary, dock, "Your box"). Arrows move within the group they were pressed in and select, like the sizes; the
       click repaints the group and rerender() puts focus back on the same option. */
    root.addEventListener("keydown", function (e) {
      var t = e.target.closest ? e.target.closest("[data-ioc-plan][role='radio']") : null;
      if (!t) return;
      var keys = ["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp", "Home", "End"];
      if (keys.indexOf(e.key) === -1) return;
      var g = t.closest("[role='radiogroup']");
      if (!g) return;
      e.preventDefault();
      var opts = Array.prototype.slice.call(g.querySelectorAll("[data-ioc-plan][role='radio']"));
      var idx = opts.indexOf(t), nx;
      if (e.key === "Home") nx = 0;
      else if (e.key === "End") nx = opts.length - 1;
      else nx = (e.key === "ArrowRight" || e.key === "ArrowDown") ? (idx + 1) % opts.length : (idx - 1 + opts.length) % opts.length;
      opts[nx].focus();
      opts[nx].click();
    });
    function statusText(n) {
      if (!status) return;
      var tpl = n === 0 ? status.getAttribute("data-empty") : (n >= max ? (status.getAttribute("data-full") || status.getAttribute("data-filled")) : status.getAttribute("data-filled"));
      status.textContent = fmt(tpl || "{n} / {max}", { n: String(Math.min(n, max)), max: String(max) });
    }

    /* ---------- fallback + legacy: the grid adds singles natively, slots mirror the real cart ---------- */
    if (mode !== "box") {
      var lastCount = -1;
      function paint(cart) {
        var filled = [];
        (cart.items || []).forEach(function (it) { for (var i = 0; i < it.quantity; i++) filled.push(it); });
        if (slots) {
          var html = "";
          for (var i = 0; i < max; i++) {
            var it = filled[i];
            if (it && it.image) html += '<div class="slot filled"><img src="' + thumb(it.image) + '" alt="' + esc(it.product_title) + '"></div>';
            else html += '<div class="slot">' + pad(i + 1) + "</div>";
          }
          slots.innerHTML = html;
        }
        statusText(filled.length);
        if (lastCount > -1 && filled.length > lastCount) {
          var newest = filled[filled.length - 1];
          var tAdded = noteEl ? noteEl.getAttribute("data-added") : "";
          var tFull = noteEl ? noteEl.getAttribute("data-full") : "";
          if (filled.length >= max) note(fmt(tFull, { max: String(max) }));
          else if (newest) note(fmt(tAdded, { flavor: newest.product_title }));
        }
        lastCount = filled.length;
      }
      var last = null;
      function refresh() { cartJson().then(function (c) { last = c; paint(c); }).catch(function () {}); }
      function onCartEvent(e) { afterCartEvent(e).then(refresh, refresh); setTimeout(refresh, 1500); }
      document.addEventListener("shopify:cart:lines-update", onCartEvent);
      document.addEventListener("cart:update", onCartEvent);
      tierBtns.forEach(function (b) { b.addEventListener("click", function () { setTier(Number(b.getAttribute("data-ioc-tier"))); if (last) paint(last); else refresh(); }); });
      refresh();
      var addParam = params.get("add");
      if (addParam) {
        var card = root.querySelector('.bx-card[data-handle="' + CSS.escape(addParam) + '"]');
        var btn = card && card.querySelector("button[name='add']:not([disabled])");
        history.replaceState(null, "", window.location.pathname + window.location.hash);
        if (btn) setTimeout(function () { btn.click(); }, 400);
      }
      return;
    }

    /* =================================================== box mode ==== */
    var data = {};
    try { data = JSON.parse(root.querySelector("[data-ioc-box-data]").textContent); } catch (e) { return; }
    var T = data.t || {};
    var KEY = "ioc-box";
    var ybId = root.getAttribute("data-yourbox");
    var gfcId = root.getAttribute("data-gfconfirm");
    var dock = root.querySelector("[data-ioc-dock]");
    var boxForm = root.querySelector("[data-ioc-box-form]");
    var surchargeForm = root.querySelector("[data-ioc-surcharge-form]");
    var addBtn = root.querySelector("[data-ioc-box-add]");
    var variantInput = root.querySelector("[data-ioc-box-variant]");
    var flavorsInput = root.querySelector("[data-ioc-box-flavors]");
    var premiumInput = root.querySelector("[data-ioc-box-premium]");
    var planInput = root.querySelector("[data-ioc-box-plan]");
    var giftToInput = root.querySelector("[data-ioc-gift-to-input]");
    var giftNoteInput = root.querySelector("[data-ioc-gift-note-input]");
    // getAttribute, not .id: a form's named controls shadow its properties and this form has an input named "id".
    var formId = boxForm && boxForm.querySelector("form") ? boxForm.querySelector("form").getAttribute("id") || "" : "";
    var typeBtns = Array.prototype.slice.call(root.querySelectorAll("[data-ioc-box-type]"));
    var types = typeBtns.map(function (b) { return b.getAttribute("data-ioc-box-type"); });
    var tiers = tierBtns.map(function (b) { return Number(b.getAttribute("data-ioc-tier")); });
    var tiersSorted = tiers.slice().sort(function (a, b) { return a - b; });
    var cards = Array.prototype.slice.call(root.querySelectorAll(".bx-card[data-handle]"));
    var flavor = {};
    cards.forEach(function (c) {
      var h = c.getAttribute("data-handle");
      flavor[h] = { handle: h, title: c.getAttribute("data-title") || h, image: c.getAttribute("data-image") || "", premium: c.getAttribute("data-premium") === "1", gluten: c.getAttribute("data-gluten") === "1", sold: c.getAttribute("data-sold") === "1", pickable: !!c.querySelector("[data-ioc-pick]") };
    });
    function pickOf(h) { var f = flavor[h]; return { handle: h, title: f.title, image: f.image, premium: f.premium }; }
    var pending = null;   // a flavor waiting to swap in while the box is full
    var adding = false;
    var gfTarget = null;

    var state = { tier: max, type: types[0] || "", plan: "", planName: "", picks: [], gift: { on: false, to: "", note: "" } };
    try {
      var saved = JSON.parse(window.sessionStorage.getItem(KEY) || "null");
      if (saved && Array.isArray(saved.picks)) {
        state.tier = Number(saved.tier) || max;
        state.type = saved.type || state.type;
        state.plan = saved.plan ? String(saved.plan) : "";
        state.planName = saved.planName || "";
        state.picks = saved.picks;
        if (saved.gift) state.gift = { on: !!saved.gift.on, to: String(saved.gift.to || ""), note: String(saved.gift.note || "") };
      }
    } catch (e) {}
    if (tiers.indexOf(state.tier) === -1) state.tier = max;
    if (types.length && types.indexOf(state.type) === -1) state.type = types[0];
    if (!types.length) state.type = "";
    // R5/D9: a subscription is never pre-selected. A restored plan survives only if the shopper chose it this session.
    if (state.plan && !state.planName) state.plan = "";
    // Restored picks are rebuilt from the page (fresh photo, fresh premium flag); a flavor that left the grid or sold
    // out since cannot be baked, so it comes out quietly on load.
    state.picks = state.picks.filter(function (p) { return p && flavor[p.handle] && flavor[p.handle].pickable; }).map(function (p) { return pickOf(p.handle); });

    function glutenActive() { return !!(data.hasType && data.glutenTypeHandle && state.type === data.glutenTypeHandle); }
    function regularType() { for (var i = 0; i < types.length; i++) if (types[i] !== data.glutenTypeHandle) return types[i]; return types[0] || ""; }
    // A1: a session restored into Gluten Friendly can never carry a wheat pick forward.
    if (glutenActive()) state.picks = state.picks.filter(function (p) { return flavor[p.handle].gluten; });

    /* ---------- the URL contract ---------- */
    var consumed = false;
    var qTier = Number(params.get("tier"));
    if (params.has("tier")) { consumed = true; if (tiers.indexOf(qTier) > -1) state.tier = qTier; }
    /* ?type=gluten-friendly (the Subscribe page's Regular / Gluten Friendly toggle adds it). Matched against the Box's
       own Type values (handleized); "gf" and "gluten" also mean the gluten friendly type. Never takes picks out on its
       own: a restored box holding wheat picks keeps its type and the confirm sheet asks once the page is painted. */
    var qType = (params.get("type") || "").toLowerCase().trim();
    var askGf = false;
    if (params.has("type")) {
      consumed = true;
      var tWant = types.indexOf(qType) > -1 ? qType : (/^(gf|gluten)$/.test(qType) ? data.glutenTypeHandle : (qType === "regular" ? regularType() : ""));
      if (tWant && types.indexOf(tWant) > -1 && tWant !== state.type) {
        if (tWant === data.glutenTypeHandle && state.picks.some(function (p) { return !flavor[p.handle].gluten; })) askGf = true;
        else state.type = tWant;
      }
    }
    var wantPlan = (params.get("plan") || "").toLowerCase();
    if (params.has("plan")) consumed = true;
    var qGift = (params.get("gift") || "").toLowerCase();
    if (params.has("gift")) { consumed = true; if (qGift === "1" || qGift === "true" || qGift === "yes") state.gift.on = true; }
    var adds = [];
    params.getAll("add").forEach(function (v) { String(v).split(",").forEach(function (h) { h = h.trim().toLowerCase(); if (h) adds.push(h); }); });
    if (params.has("add")) consumed = true;
    if (state.picks.length > state.tier) trimTo(state.tier);
    var addNotes = [];
    adds.forEach(function (h) {
      var f = flavor[h];
      if (!f || !f.pickable) return;
      var switched = false;
      if (glutenActive() && !f.gluten) { state.type = regularType(); switched = true; }
      // Only the last toast shows, so a switch to Regular (?type=gluten-friendly&add=a wheat flavor) says so itself.
      if (state.picks.length < state.tier) { state.picks.push(pickOf(h)); addNotes.push(fmt(switched ? T.toastRegular : T.toastAdded, { flavor: f.title })); }
      else addNotes.push(fmt(T.toastFull, { max: String(state.tier) }));
    });
    if (consumed) {
      var keep = new URLSearchParams(window.location.search);
      ["tier", "plan", "add", "gift", "type"].forEach(function (k) { keep.delete(k); });
      var qs = keep.toString();
      try { history.replaceState(history.state, "", window.location.pathname + (qs ? "?" + qs : "") + window.location.hash); } catch (e) {}
    }

    /* ---------- reading the ladder ---------- */
    function save() { try { window.sessionStorage.setItem(KEY, JSON.stringify(state)); } catch (e) {} }
    function variant() {
      var list = data.variants.filter(function (v) { return v.count === state.tier && (!data.hasType || v.type === state.type); });
      return list.filter(function (v) { return v.available; })[0] || list[0] || null;
    }
    function cadenceOf(name) { var m = /every\s+[^,.;]+/i.exec(String(name || "")); return m ? m[0].trim().toLowerCase() : String(name || "").toLowerCase(); }
    /* D2-A (Cora #9): the plan in one word for the dock button, "Start monthly · $59.94 today". */
    function planWord(name) { var c = cadenceOf(name); return /^every month$/.test(c) ? "monthly" : (/^every week$/.test(c) ? "weekly" : c); }
    function cap(s) { s = String(s || ""); return s.charAt(0).toUpperCase() + s.slice(1); }
    /* Each size has its own selling plan group in Admin, so plan ids (and names, which carry the saving) differ per
       size. A chosen plan is carried across sizes by id, then by exact name, then by its schedule ("every month"). */
    function currentPlan(v) {
      if (!v) return null;
      if (wantPlan) {
        var re = /(2|two)\s*-?\s*week|biweek|fortnight/.test(wantPlan) ? /(2|two)\s*week|fortnight/i : (/month/.test(wantPlan) ? /month/i : null);
        var hit = re ? v.plans.filter(function (p) { return re.test(p.name); })[0] : null;
        if (hit) { state.plan = String(hit.id); state.planName = hit.name; }
        wantPlan = "";
      }
      if (!state.plan) return null;
      var p = v.plans.filter(function (q) { return String(q.id) === String(state.plan); })[0] || null;
      if (!p && state.planName) p = v.plans.filter(function (q) { return q.name === state.planName; })[0] || null;
      if (!p && state.planName) { var cad = cadenceOf(state.planName); p = v.plans.filter(function (q) { return cadenceOf(q.name) === cad; })[0] || null; }
      if (p) { state.plan = String(p.id); state.planName = p.name; }
      else { state.plan = ""; state.planName = ""; }
      return p;
    }
    function premiumCount() { return state.picks.filter(function (p) { return p.premium; }).length; }
    function surchargeCents() { return data.premiumCharged ? premiumCount() * (Number(data.premiumPrice) || 0) : 0; }
    function moneyOf(cents) { return fmtMoney(Number(cents || 0), data.moneyFormat, data.currency); }
    function counts() { var c = {}; state.picks.forEach(function (p) { c[p.handle] = (c[p.handle] || 0) + 1; }); return c; }
    function nextTier(n) { for (var i = 0; i < tiersSorted.length; i++) if (tiersSorted[i] > n) return tiersSorted[i]; return null; }
    function aNext(n) { return (/^(8|11|18|80|8\d)$/.test(String(n)) ? "an " : "a ") + n; }
    function flavorsLine() {
      var c = {}, order = [];
      state.picks.forEach(function (p) { if (!c[p.title]) { c[p.title] = 0; order.push(p.title); } c[p.title]++; });
      return order.map(function (t) { return c[t] + "× " + t; }).join(", ");
    }
    /* trimTo(n): when the size drops, take cookies off the biggest stack first until the box fits. Never empties it. */
    function trimTo(n) {
      var guard = 0;
      while (state.picks.length > n && guard < 500) {
        var c = {};
        state.picks.forEach(function (p) { c[p.handle] = (c[p.handle] || 0) + 1; });
        var big = Object.keys(c).sort(function (a, b) { return c[b] - c[a]; })[0];
        for (var i = state.picks.length - 1; i >= 0; i--) { if (state.picks[i].handle === big) { state.picks.splice(i, 1); break; } }
        guard++;
      }
    }

    /* ---------- painting ---------- */
    /* Replace a container's markup only when it changed, and put focus back on the same control afterwards, so a
       keyboard or screen reader user tapping a stepper inside "Your box" is not thrown back to the top. */
    function controlKey(el) {
      if (!el || !el.getAttribute) return null;
      var k = ["data-ioc-plan", "data-ioc-dec", "data-ioc-inc"].filter(function (a) { return el.hasAttribute(a); })[0];
      return k ? k + "|" + (el.getAttribute(k) || "") + "|" + (el.getAttribute("data-handle") || "") : null;
    }
    function rerender(el, html) {
      if (el.__iocHtml === html) return;
      var act = document.activeElement;
      var key = act && el.contains(act) ? controlKey(act) : null;
      el.innerHTML = html;
      el.__iocHtml = html;
      if (key) {
        var match = Array.prototype.filter.call(el.querySelectorAll("button"), function (b) { return controlKey(b) === key; })[0];
        if (match) { try { match.focus({ preventScroll: true }); } catch (e) { match.focus(); } }
      }
    }
    function paintCards() {
      var qty = counts();
      var full = state.picks.length >= state.tier;
      var gf = glutenActive();
      root.classList.toggle("is-full", full);
      root.classList.toggle("is-gf", gf);
      cards.forEach(function (card) {
        var h = card.getAttribute("data-handle");
        var q = qty[h] || 0;
        card.hidden = gf && !flavor[h].gluten;
        card.classList.toggle("is-picked", q > 0);
        var badge = card.querySelector("[data-ioc-card-qty]");
        if (badge) { badge.hidden = q === 0; badge.textContent = "×" + q; }
      });
      Array.prototype.forEach.call(root.querySelectorAll("[data-ioc-pick][data-handle]"), function (b) {
        var h = b.getAttribute("data-handle"), f = flavor[h];
        if (!f) return;
        b.hidden = (qty[h] || 0) > 0;
        b.classList.toggle("is-swap", full);
        if (!b.hasAttribute("data-label")) b.setAttribute("data-label", b.textContent.trim());
        var lbl = b.querySelector("[data-ioc-add-label]");
        var txt = full ? T.swapLabel : (lbl ? T.addLabel : b.getAttribute("data-label"));
        if (lbl) { if (lbl.textContent !== txt) lbl.textContent = txt; } else if (b.textContent !== txt) b.textContent = txt;
        b.setAttribute("aria-label", full ? T.swapLabel + ": " + f.title : "Add " + f.title + " to your box");
      });
      Array.prototype.forEach.call(root.querySelectorAll("[data-ioc-stepper][data-handle]"), function (st) {
        var h = st.getAttribute("data-handle"), q = qty[h] || 0;
        st.hidden = q === 0;
        st.classList.toggle("is-full", full);
        var out = st.querySelector("[data-ioc-qty]");
        if (out) out.textContent = String(q);
        var inc = st.querySelector("[data-ioc-inc]");
        if (inc && flavor[h]) inc.setAttribute("aria-label", full ? T.swapLabel + ": " + flavor[h].title : "Add one more " + flavor[h].title + ", " + q + " in your box");
      });
      placeGfNote();
    }
    /* D2-A (Buggy P1-7): the gluten friendly note sits after the first row of cards, not above the grid, so the first
       row's "+ Add" stays above the dock at 390 and 375 (the three-line note used to push it behind). */
    function placeGfNote() {
      var gn = root.querySelector("[data-ioc-gluten-note]");
      var grid = root.querySelector("[data-ioc-grid]");
      if (!gn || !grid || !glutenActive()) return;
      var cols = (getComputedStyle(grid).gridTemplateColumns || "").split(" ").filter(Boolean).length || 2;
      var shown = cards.filter(function (c) { return !c.hidden; });
      var anchor = shown[cols] || null;
      if (gn.parentNode !== grid || gn.nextElementSibling !== anchor) grid.insertBefore(gn, anchor);
      gn.classList.add("is-in-grid");
    }
    window.addEventListener("resize", function () { placeGfNote(); }, { passive: true });

    /* D2-A (Buggy P1-4 / P2): nothing a keyboard user focuses ever sits under the dock (or under the sticky header). */
    root.addEventListener("focusin", function (e) {
      var t = e.target;
      if (!dockOn() || !t || !t.getBoundingClientRect || dock.contains(t) || t.closest("dialog, .ioc-sheet")) return;
      var r = t.getBoundingClientRect();
      var limit = dock.getBoundingClientRect().top - 12;
      var hdr = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--ioc-hdr-visible")) || 0;
      // instant: the theme scrolls smoothly, and an animated nudge would leave the item under the dock for a beat
      if (r.bottom > limit) window.scrollBy({ top: r.bottom - limit, behavior: "instant" });
      else if (r.top < hdr + 8) window.scrollBy({ top: r.top - hdr - 8, behavior: "instant" });
    });
    /* "Skip to your box": one Tab from the controls past every card to the delivery choice and the add button. */
    root.addEventListener("click", function (e) {
      var sk = e.target.closest && e.target.closest("[data-ioc-skip-box]");
      if (!sk) return;
      e.preventDefault();
      var scope = dockOn() ? dock : root.querySelector("[data-ioc-summary]");
      var c = scope && (scope.querySelector('[data-ioc-plan][aria-checked="true"]') || scope.querySelector("[data-ioc-go]"));
      if (c) { try { c.focus({ preventScroll: !dockOn() ? false : true }); } catch (err) { c.focus(); } }
    });
    function pipsHtml(m, all) {
      var h = "";
      if (m <= 12 || all) {
        for (var i = 0; i < m; i++) {
          var p = state.picks[i];
          h += p ? '<span class="bx-pip on' + (p.premium ? " prem" : "") + '">' + (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy">' : "") + "</span>" : '<span class="bx-pip"></span>';
        }
        return h;
      }
      var pct = Math.round(Math.min(1, state.picks.length / m) * 100);
      h += '<span class="bx-bar"><i style="width:' + pct + '%"></i></span><span class="bx-last">';
      state.picks.slice(-6).forEach(function (p) { h += '<span class="bx-pip on">' + (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy">' : "") + "</span>"; });
      return h + "</span>";
    }
    function choiceHtml(v, plan, inDock) {
      if (!v) return "";
      /* D-Nick (2026-09-25): the premium add-on is a one-time-box charge only. Every subscription button shows the
         plain plan price; only the one-time button carries the surcharge. */
      var sur = surchargeCents();
      var h = '<div class="bx-choice" role="radiogroup" aria-label="' + esc(T.choiceLabel) + '">';
      /* Final-B: a roving tabindex like the size radios. One Tab stop for the group, on the checked option (or one-time
         when the saved plan is not offered on this box); the arrow keys move and select (keydown below). */
      var planOn = !!(plan && v.plans.some(function (p) { return String(p.id) === String(plan.id); }));
      h += '<button type="button" role="radio" data-ioc-plan="" aria-checked="' + (plan ? "false" : "true") + '" tabindex="' + (planOn ? "-1" : "0") + '"><span class="bx-choice__k">' + esc(T.oneTime) + '</span><span class="bx-choice__p">' + esc(moneyOf(v.price + sur)) + "</span></button>";
      v.plans.forEach(function (p) {
        var on = plan && String(plan.id) === String(p.id);
        h += '<button type="button" role="radio" data-ioc-plan="' + p.id + '" aria-checked="' + (on ? "true" : "false") + '" tabindex="' + (on ? "0" : "-1") + '"><span class="bx-choice__k">' + esc(cap(cadenceOf(p.name))) + '</span><span class="bx-choice__p">' + esc(moneyOf(p.price)) + "</span>" +
          (p.saving > 0 ? '<span class="bx-choice__s">' + esc(fmt(T.save, { amount: p.savingF })) + "</span>" : "") +
          /* D2-A (Cora #9): the plan says its whole value where the thumb is. Text from Theme settings (Ships free). */
          (data.shipFree ? '<span class="bx-choice__f">' + esc(String(data.shipFree).toLowerCase()) + "</span>" : "") + "</button>";
      });
      h += "</div>";
      /* The disclosure sits between the choice and the button, always, and states exactly what is charged. The
         premium add-on is a one-time-box charge only: a subscription is never charged it, first box or renewal, so
         the plan branch never mentions it (renewSubPrem is unused; the field stays in Theme settings unused too). */
      var d;
      if (plan) {
        var cad = cadenceOf(plan.name);
        d = esc(fmt(T.renewSub, { cadence: cad, amount: moneyOf(plan.price) }));
        // The dock has 230px to carry all of this; "Ships free" stays in Your box and the desktop summary.
        if (data.shipFree && !inDock) d += " " + esc(data.shipFree) + ".";
        if (T.renewNote) d += " " + esc(T.renewNote);
        if (T.cancelLabel) d += ' <a href="' + esc(data.cancelUrl) + '">' + esc(T.cancelLabel) + "</a>.";
        if (T.termsLabel) d += ' <a href="' + esc(data.termsUrl) + '">' + esc(T.termsLabel) + "</a>";
      } else {
        d = esc(sur ? fmt(T.renewOnePrem, { amount: moneyOf(v.price + sur), premium: moneyOf(sur) }) : fmt(T.renewOne, { amount: moneyOf(v.price) }));
      }
      return h + '<p class="bx-disc' + (plan ? " is-sub" : "") + '">' + d + "</p>";
    }
    function ybListHtml() {
      var c = counts(), order = [];
      state.picks.forEach(function (p) { if (order.indexOf(p.handle) === -1) order.push(p.handle); });
      var full = state.picks.length >= state.tier;
      return order.map(function (h) {
        var f = flavor[h];
        return '<li class="bx-yb__row"><span class="bx-yb__img">' + (f.image ? '<img src="' + esc(f.image) + '" alt="" loading="lazy">' : "") + '</span>' +
          /* D-Nick (2026-09-25): the +$ tag is a one-time-box charge; a subscription never pays it. */
          '<span class="bx-yb__name">' + esc(f.title) + (f.premium && data.premiumCharged && !state.plan ? ' <span class="bx-yb__prem mono">+' + esc(moneyOf(data.premiumPrice)) + "</span>" : "") + "</span>" +
          '<span class="bx-step bx-step--sm' + (full ? " is-full" : "") + '"><button type="button" data-ioc-dec="" data-handle="' + esc(h) + '" aria-label="Take one ' + esc(f.title) + ' out">&minus;</button><output>' + c[h] + '</output><button type="button" data-ioc-inc="" data-handle="' + esc(h) + '" aria-label="' + esc(full ? T.swapLabel + ": " + f.title : "Add one more " + f.title) + '">+</button></span></li>';
      }).join("");
    }
    function paintBox() {
      var v = variant();
      var plan = currentPlan(v);
      var n = state.picks.length, m = state.tier, full = n >= m;
      /* D-Nick (2026-09-25): the premium add-on is one-time-box only, so it never lands in a plan's "today" price. */
      var sur = surchargeCents();
      var today = v ? (plan ? plan.price : v.price + sur) : 0;
      var ready = !!(full && v && v.available);

      Array.prototype.forEach.call(root.querySelectorAll("[data-ioc-count]"), function (el) {
        el.textContent = full ? fmt(T.countFull, { max: String(m) }) : fmt(T.count, { n: String(n), max: String(m) });
      });
      Array.prototype.forEach.call(root.querySelectorAll("[data-ioc-pips]"), function (el) {
        rerender(el, pipsHtml(m, !!el.closest("[data-ioc-summary]")));
        el.classList.toggle("is-bar", m > 12 && !el.closest("[data-ioc-summary]"));
      });
      var price = root.querySelector("[data-ioc-price]");
      var gift = !!state.gift.on;
      if (price && v) {
        price.querySelector("b").textContent = moneyOf(today);
        var when = plan ? cadenceOf(plan.name) : String(T.oneTime || "").toLowerCase();
        /* D2-A (Cora #7): gift mode says so in the dock. */
        price.querySelector("span").textContent = gift ? (T.giftBox || "Gift box") + " · " + when : when;
      }
      var label;
      if (adding) label = T.adding;
      else if (!v) label = "";
      else if (full && !v.available) label = T.soldOut;
      else if (n === 0) label = fmt(T.pick, { n: String(m) });
      else if (!full) label = fmt(T.more, { n: String(m - n) });
      else if (plan) label = fmt(T.start, { amount: moneyOf(today), cadence: cadenceOf(plan.name), plan: planWord(plan.name) });
      else label = fmt(gift && T.addGift ? T.addGift : T.add, { amount: moneyOf(today) });
      Array.prototype.forEach.call(root.querySelectorAll("[data-ioc-go]"), function (b) {
        if (b.textContent !== label) b.textContent = label;
        b.classList.toggle("is-ready", ready);
        b.disabled = adding || !!(full && v && !v.available);
      });
      if (dock) dock.classList.toggle("is-full", full);
      /* The delivery choice lives in the dock only once the box is full (the decision happens where the thumb already
         is), and always in "Your box" and the desktop summary. */
      Array.prototype.forEach.call(root.querySelectorAll("[data-ioc-choicewrap]"), function (w) {
        var inDock = !!w.closest("[data-ioc-dock]");
        rerender(w, inDock && !full ? "" : choiceHtml(v, plan, inDock));
      });
      // "Your box"
      var list = root.querySelector("[data-ioc-yb-list]");
      if (list) rerender(list, ybListHtml());
      var empty = root.querySelector("[data-ioc-yb-empty]");
      if (empty) empty.hidden = n > 0;
      var clear = root.querySelector("[data-ioc-box-clear]");
      if (clear) clear.hidden = n === 0;
      var fullBox = root.querySelector("[data-ioc-yb-full]");
      if (fullBox) {
        var nx = nextTier(m);
        fullBox.hidden = !(full && pending);
        var txt = nx ? fmt(T.full, { max: String(m), a_next: aNext(nx) }) : fmt(T.fullTop, { max: String(m) });
        if (pending && flavor[pending]) txt += " " + fmt(T.swapPending, { flavor: flavor[pending].title });
        fullBox.querySelector("[data-ioc-yb-full-text]").textContent = txt;
        var up = fullBox.querySelector("[data-ioc-upsize]");
        up.hidden = !nx;
        if (nx) { up.textContent = fmt(T.upsize, { a_next: aNext(nx) }); up.setAttribute("data-ioc-upsize", String(nx)); }
      }
      // the real form
      if (variantInput) variantInput.value = v ? v.id : "";
      if (flavorsInput) flavorsInput.value = flavorsLine();
      var pc = premiumCount();
      if (premiumInput) { premiumInput.value = String(pc); premiumInput.disabled = pc === 0; }
      if (planInput) { planInput.value = plan ? String(plan.id) : ""; planInput.disabled = !plan; }
      var giftOn = !!(state.gift.on && (state.gift.to.trim() || state.gift.note.trim()));
      if (giftToInput) { giftToInput.value = state.gift.to.trim(); giftToInput.disabled = !(giftOn && state.gift.to.trim()); }
      if (giftNoteInput) { giftNoteInput.value = state.gift.note.trim(); giftNoteInput.disabled = !(giftOn && state.gift.note.trim()); }
      if (surchargeForm) { var q = surchargeForm.querySelector("[data-ioc-surcharge-qty]"); if (q) q.value = String(Math.max(1, pc)); }
      // Never disabled for "adding": the go buttons carry that. Horizon refuses to submit while this is disabled.
      if (addBtn) addBtn.disabled = !ready;
    }
    function paintSizes() {
      tierBtns.forEach(function (b) {
        var n = Number(b.getAttribute("data-ioc-tier"));
        var v = data.variants.filter(function (x) { return x.count === n && (!data.hasType || x.type === state.type); })[0];
        var el = b.querySelector("[data-ioc-tier-price]");
        if (el && v && el.textContent !== v.priceF) el.textContent = v.priceF;
      });
      typeBtns.forEach(function (b) {
        var on = b.getAttribute("data-ioc-box-type") === state.type;
        b.classList.toggle("is-on", on);
        b.setAttribute("aria-pressed", on ? "true" : "false");
      });
      var gn = root.querySelector("[data-ioc-gluten-note]");
      if (gn) gn.hidden = !glutenActive();
    }
    function paintGift() {
      Array.prototype.forEach.call(root.querySelectorAll("[data-ioc-gift]"), function (w) {
        var on = w.querySelector("[data-ioc-gift-on]"); if (!on) return;
        on.checked = state.gift.on;
        w.querySelector("[data-ioc-gift-fields]").hidden = !state.gift.on;
        var to = w.querySelector("[data-ioc-gift-to]"), nt = w.querySelector("[data-ioc-gift-note]");
        if (to !== document.activeElement && to.value !== state.gift.to) to.value = state.gift.to;
        if (nt !== document.activeElement && nt.value !== state.gift.note) nt.value = state.gift.note;
        var cnt = w.querySelector("[data-ioc-gift-count]");
        if (cnt) cnt.textContent = state.gift.note.length + " / " + (nt.getAttribute("maxlength") || 240);
      });
    }
    function paintAll() { setTier(state.tier); paintSizes(); paintCards(); paintBox(); paintGift(); save(); measureDock(); afterPaint(); }

    /* D2-A. The moment the box fills (not on a restored full box): in gift mode "Your box" opens at the gift note with
       the cursor in "Who it's for" (Cora #7); otherwise a keyboard user lands on the delivery choice instead of
       tabbing through every card left in the grid (Buggy P1-4). */
    var lastFull = null, lastOpener = null, giftAsked = false;
    function afterPaint() {
      var full = state.picks.length >= state.tier;
      if (lastFull === false && full) onFilled();
      lastFull = full;
    }
    function dockOn() { return !!(dock && dock.getClientRects().length && getComputedStyle(dock).display !== "none"); }
    function openGift(opener) {
      giftAsked = true;
      var target;
      if (dockOn() && window.IOCSheet && ybId) {
        if (!window.IOCSheet.isOpen(ybId)) window.IOCSheet.open(ybId, opener || document.activeElement);
        var sheet = document.getElementById(ybId);
        target = sheet && sheet.querySelector('[data-ioc-gift="sheet"]');
      } else {
        target = root.querySelector('[data-ioc-gift="side"]');
      }
      if (!target) return;
      var on = target.querySelector("[data-ioc-gift-on]");
      if (on && !on.checked) { state.gift.on = true; paintGift(); paintBox(); save(); }
      var to = target.querySelector("[data-ioc-gift-to]"), nt = target.querySelector("[data-ioc-gift-note]");
      var f = to && !to.value.trim() ? to : nt;
      setTimeout(function () { if (f) { try { f.focus(); } catch (e) {} if (f.scrollIntoView && !dockOn()) f.scrollIntoView({ block: "center" }); } }, 320);
    }
    function onFilled() {
      var opener = lastOpener; lastOpener = null;
      if (state.gift.on && root.querySelector("[data-ioc-gift]")) { openGift(opener); return; }
      var kb = false;
      try { kb = !!(opener && opener.matches(":focus-visible")); } catch (e) {}
      if (!kb) return;
      var scope = dockOn() ? dock : root.querySelector("[data-ioc-summary]");
      var c = scope && (scope.querySelector('[data-ioc-plan][aria-checked="true"]') || scope.querySelector("[data-ioc-plan]") || scope.querySelector("[data-ioc-go]"));
      if (c) { try { c.focus({ preventScroll: true }); } catch (e) { c.focus(); } }
    }

    /* The gift note is built once per place it shows (desktop summary, "Your box"), never re-rendered, so typing is
       never interrupted. Both copies write one state; only the hidden inputs inside the form are ever submitted. */
    Array.prototype.forEach.call(root.querySelectorAll("[data-ioc-gift]"), function (w, i) {
      var id = "IocGift-" + (root.getAttribute("data-yourbox") || "b") + "-" + i;
      w.innerHTML = '<div class="bx-gift"><label class="bx-gift__toggle" for="' + id + '-on"><input type="checkbox" id="' + id + '-on" data-ioc-gift-on aria-controls="' + id + '-f"><span>' + esc(T.giftLabel) + "</span></label>" +
        '<div class="bx-gift__fields" id="' + id + '-f" data-ioc-gift-fields hidden>' +
        '<label for="' + id + '-to">' + esc(T.giftTo) + '</label><input type="text" id="' + id + '-to" maxlength="60" autocomplete="off" placeholder="' + esc(T.giftToPh) + '" data-ioc-gift-to>' +
        '<label for="' + id + '-note">' + esc(T.giftNote) + '</label><textarea id="' + id + '-note" maxlength="240" rows="3" placeholder="' + esc(T.giftNotePh) + '" data-ioc-gift-note></textarea>' +
        '<span class="bx-gift__count mono" data-ioc-gift-count aria-hidden="true"></span></div></div>';
    });
    root.addEventListener("change", function (e) {
      if (e.target && e.target.matches && e.target.matches("[data-ioc-gift-on]")) { state.gift.on = e.target.checked; paintGift(); paintBox(); save(); }
    });
    root.addEventListener("input", function (e) {
      var t = e.target;
      if (!t || !t.matches) return;
      if (t.matches("[data-ioc-gift-to]")) state.gift.to = t.value;
      else if (t.matches("[data-ioc-gift-note]")) {
        /* Final-B: a note with a line break is posted as JSON (the add button below), which keeps each break as ONE
           \n, so the note, the counter and the textarea's own maxlength all count a break as one character. */
        var gmax = Number(t.getAttribute("maxlength")) || 240;
        var gv = t.value.replace(/\r\n?/g, "\n");
        if (gv.length > gmax) gv = gv.slice(0, gmax);
        if (gv !== t.value) t.value = gv;
        state.gift.note = gv;
      }
      else return;
      paintGift(); paintBox(); save();
    });

    /* ---------- the dock's footprint ---------- */
    function measureDock() {
      if (!dock) return;
      var h = dock.getClientRects().length ? dock.offsetHeight : 0;
      document.documentElement.style.setProperty("--ioc-dock-h", h + "px");
      document.documentElement.classList.toggle("ioc-has-dock", h > 0);
    }
    window.addEventListener("resize", measureDock, { passive: true });
    if (dock && "ResizeObserver" in window) new ResizeObserver(measureDock).observe(dock);
    document.addEventListener("ioc:tab", function () { setTimeout(measureDock, 0); });

    /* ---------- actions ---------- */
    function closeSheets() {
      if (!window.IOCSheet) return;
      [ybId, gfcId].forEach(function (id) { if (id && window.IOCSheet.isOpen(id)) window.IOCSheet.close(id); });
      Array.prototype.forEach.call(root.querySelectorAll(".ioc-fs-sheet[open]"), function (d) { window.IOCSheet.close(d.id); });
    }
    function add(h, opener) {
      var f = flavor[h];
      if (!f || !f.pickable) return;
      if (glutenActive() && !f.gluten) return;
      if (state.picks.length >= state.tier) { openFull(h, opener); return; }
      state.picks.push(pickOf(h));
      lastOpener = opener || null;
      var hadFocus = !!(opener && opener === document.activeElement);
      paintAll();
      note(state.picks.length >= state.tier ? fmt(T.toastFull, { max: String(state.tier) }) : fmt(T.toastAdded, { flavor: f.title }));
      /* D2-A (Buggy P1-4): the first pick hides "+ Add" and shows the stepper; focus follows to that card's "+" instead
         of dropping to the page. (Skipped when the box just filled: onFilled() already placed focus.) */
      var act = document.activeElement;
      /* WebKit keeps the just-hidden button as activeElement until its next style pass, so act === opener counts as lost. */
      if (hadFocus && opener.hidden && (!act || act === document.body || act === opener || !root.contains(act))) {
        var sc = opener.closest(".bx-card, .ioc-fs-sheet, dialog") || root;
        var inc = sc.querySelector('[data-ioc-stepper][data-handle="' + (window.CSS && CSS.escape ? CSS.escape(h) : h) + '"]:not([hidden]) [data-ioc-inc]');
        if (inc) { try { inc.focus({ preventScroll: true }); } catch (e) { inc.focus(); } }
      }
    }
    function removeOne(h) {
      for (var i = state.picks.length - 1; i >= 0; i--) {
        if (state.picks[i].handle === h) {
          state.picks.splice(i, 1);
          if (pending && flavor[pending] && state.picks.length < state.tier) {
            var sw = pending; pending = null;
            state.picks.push(pickOf(sw));
            paintAll();
            note(fmt(T.toastSwapped, { flavor: flavor[sw].title }));
            return;
          }
          paintAll();
          note(fmt(T.toastRemoved, { flavor: flavor[h] ? flavor[h].title : "" }));
          return;
        }
      }
    }
    function openFull(h, opener) {
      pending = h;
      paintBox();
      if (window.IOCSheet && ybId) {
        if (!window.IOCSheet.isOpen(ybId)) {
          Array.prototype.forEach.call(root.querySelectorAll(".ioc-fs-sheet[open]"), function (d) { window.IOCSheet.close(d.id); });
          window.IOCSheet.open(ybId, opener);
        }
      }
      note(fmt(T.toastFull, { max: String(state.tier) }));
    }
    function changeTier(nt) {
      if (nt === state.tier || tiers.indexOf(nt) === -1) return;
      var before = state.picks.length;
      state.tier = nt;
      if (state.picks.length > nt) trimTo(nt);
      if (pending && flavor[pending] && state.picks.length < nt) {
        var sw = pending; pending = null;
        state.picks.push(pickOf(sw));
        paintAll();
        note(fmt(T.toastSwapped, { flavor: flavor[sw].title }));
        return;
      }
      paintAll();
      if (before > state.picks.length) note(fmt(T.toastTrim, { max: String(nt) }));
    }
    function requestType(nt) {
      if (nt === state.type) return;
      if (nt === data.glutenTypeHandle) {
        var bad = state.picks.filter(function (p) { return !flavor[p.handle].gluten; }).length;
        if (bad > 0 && window.IOCSheet && gfcId) {
          gfTarget = nt;
          var tx = root.querySelector("[data-ioc-gfc-text]");
          if (tx) tx.textContent = fmt(tx.getAttribute("data-tpl"), { n: String(bad), max: String(state.picks.length) });
          window.IOCSheet.open(gfcId, document.activeElement);
          return;
        }
      }
      state.type = nt;
      paintAll();
    }
    function duckPicks() {
      /* D2-A (Cora #3): Duck picks never chooses a premium flavor, so the price is the one on the button the shopper
         tapped. A premium cookie only goes in when the shopper picks it. */
      var pool = cards.filter(function (c) { var f = flavor[c.getAttribute("data-handle")]; return f.pickable && !f.premium && (!glutenActive() || f.gluten); })
        .map(function (c) { return c.getAttribute("data-handle"); });
      if (!pool.length || state.picks.length >= state.tier) { if (state.picks.length >= state.tier) note(fmt(T.toastFull, { max: String(state.tier) })); return; }
      for (var i = pool.length - 1; i > 0; i--) { var j = Math.floor(Math.random() * (i + 1)); var tmp = pool[i]; pool[i] = pool[j]; pool[j] = tmp; }
      var got = {}, order = [], k = 0;
      while (state.picks.length < state.tier) {
        var h = pool[k % pool.length]; k++;
        state.picks.push(pickOf(h));
        if (!got[h]) { got[h] = 0; order.push(h); } got[h]++;
      }
      paintAll();
      // Two names and a count keep the toast to two lines; the full list is one tap away in "Your box".
      var names = order.slice(0, 2).map(function (h) { return (got[h] > 1 ? got[h] + "× " : "") + flavor[h].title; }).join(", ");
      if (order.length > 2) names += " and " + (order.length - 2) + " more";
      note(fmt(T.toastDuck, { flavor: names }));
    }
    function go() {
      var v = variant();
      var full = state.picks.length >= state.tier;
      if (adding) return;
      if (full && v && v.available && addBtn && !addBtn.disabled) {
        /* D2-A (Cora #7): a gift box never leaves the builder without the shopper having seen the note. If gift mode is
           on and nothing is written yet, the first Add opens the note (cursor in "Who it's for"); a second Add sends it
           as it is. Whatever was typed always rides along (hidden inputs, paintBox). */
        if (state.gift.on && !giftAsked && !state.gift.to.trim() && !state.gift.note.trim() && root.querySelector("[data-ioc-gift]")) { openGift(document.activeElement); return; }
        closeSheets(); submit(); return;
      }
      if (full) return;
      closeSheets();
      var grid = root.querySelector("[data-ioc-grid]");
      if (!grid) return;
      grid.scrollIntoView({ behavior: motionOff() ? "auto" : "smooth", block: "start" });
      var first = Array.prototype.filter.call(grid.querySelectorAll("[data-ioc-pick]"), function (b) { return !b.hidden && b.getClientRects().length && !b.closest("[hidden]"); })[0];
      if (first) setTimeout(function () { try { first.focus({ preventScroll: true }); } catch (e) {} }, motionOff() ? 0 : 450);
    }
    var addTimer;
    function submit() {
      adding = true;
      paintBox();
      clearTimeout(addTimer);
      addTimer = setTimeout(function () { if (adding) { adding = false; paintBox(); } }, 9000);
      addBtn.click();
    }

    root.addEventListener("click", function (e) {
      if (!e.target.closest) return;
      var t = e.target.closest("[data-ioc-pick], [data-ioc-inc], [data-ioc-dec], [data-ioc-tier], [data-ioc-box-type], [data-ioc-plan], [data-ioc-go], [data-ioc-upsize], [data-ioc-box-clear], [data-ioc-gf-keep], [data-ioc-gf-switch], [data-duck-picks]");
      if (!t || !root.contains(t)) return;
      if (t.hasAttribute("data-ioc-pick") || t.hasAttribute("data-ioc-inc")) { add(t.getAttribute("data-handle"), t); return; }
      if (t.hasAttribute("data-ioc-dec")) {
        var hadF = t === document.activeElement;
        removeOne(t.getAttribute("data-handle"));
        // D2-A: the last one out hides the stepper; focus goes back to that card's "+ Add".
        if (hadF && t.closest("[hidden]")) {
          var card = t.closest(".bx-card");
          var pk = card && card.querySelector("[data-ioc-pick]:not([hidden])");
          if (pk) { try { pk.focus({ preventScroll: true }); } catch (e) { pk.focus(); } }
        }
        return;
      }
      if (t.hasAttribute("data-ioc-tier")) { changeTier(Number(t.getAttribute("data-ioc-tier"))); return; }
      if (t.hasAttribute("data-ioc-box-type")) { requestType(t.getAttribute("data-ioc-box-type")); return; }
      if (t.hasAttribute("data-ioc-plan")) {
        var id = t.getAttribute("data-ioc-plan");
        state.plan = id; state.planName = "";
        if (id) { var v = variant(); var p = v && v.plans.filter(function (q) { return String(q.id) === id; })[0]; state.planName = p ? p.name : ""; if (!p) state.plan = ""; }
        paintBox(); save();
        return;
      }
      if (t.hasAttribute("data-ioc-go")) { go(); return; }
      if (t.hasAttribute("data-ioc-upsize")) { changeTier(Number(t.getAttribute("data-ioc-upsize"))); return; }
      if (t.hasAttribute("data-ioc-box-clear")) { state.picks = []; pending = null; paintAll(); note(T.toastCleared); return; }
      if (t.hasAttribute("data-ioc-gf-keep")) { gfTarget = null; if (window.IOCSheet) window.IOCSheet.close(gfcId); return; }
      if (t.hasAttribute("data-ioc-gf-switch")) {
        if (gfTarget) {
          var dropped = state.picks.filter(function (p) { return !flavor[p.handle].gluten; });
          state.type = gfTarget; gfTarget = null;
          state.picks = state.picks.filter(function (p) { return flavor[p.handle].gluten; });
          paintAll();
          if (dropped.length) note(fmt(T.toastRemoved, { flavor: dropped.length + (dropped.length === 1 ? " cookie" : " cookies") }));
        }
        if (window.IOCSheet) window.IOCSheet.close(gfcId);
        return;
      }
      if (t.hasAttribute("data-duck-picks")) { lastOpener = t; duckPicks(); return; }
    });
    document.addEventListener("ioc:sheet-close", function (e) {
      var id = e.detail && e.detail.id;
      if (id === ybId && pending) { pending = null; paintBox(); }
      if (id === gfcId) gfTarget = null;
    });

    /* B4 row 6 (SA sa-cart.js port): the Box line and the premium surcharge line go to /cart/add.js as ONE items array,
       which Shopify commits or rejects as a unit. Replaces the old second product-form submit behind sleep(300), which
       could leave a Box with no surcharge (customer underpays) or a surcharge with no Box. Used only where two lines
       are needed; a plain box still goes through the native product form. Horizon binds the form's submit handler at
       the document level in capture phase, so the native path is bypassed by cancelling the button's click default.
       D-Nick (2026-09-25): subscribers never pay the premium add-on, on this box or on renewals (the surcharge line
       has no selling plan, so it could never recur anyway). A subscription pick is a single-line native add; only a
       one-time box with premium flavors needs the second line. */
    function needsAtomic() { return !!(surchargeForm && premiumCount() > 0 && !state.plan && surchargeForm.querySelector("input[name='id']")); }
    /* Final-B: a multipart form post (Horizon's product form) sends every line break as CRLF, so a gift note with a
       break goes through the same JSON add as a premium box, where it stays one \n. */
    function noteHasBreak() { return !!(giftNoteInput && !giftNoteInput.disabled && /[\r\n]/.test(giftNoteInput.value)); }
    function boxItem() {
      var form = boxForm.querySelector("form");
      var fd = new FormData(form);
      var props = {};
      fd.forEach(function (v, k) { var m = /^properties\[(.+)\]$/.exec(k); if (m && String(v) !== "") props[m[1]] = String(v).replace(/\r\n?/g, "\n"); });
      var box = { id: Number(fd.get("id")), quantity: 1, properties: props };
      var plan = fd.get("selling_plan");
      if (plan) box.selling_plan = Number(plan);
      return box;
    }
    function atomicItems() {
      var box = boxItem();
      if (!needsAtomic()) return [box];
      /* D2-A (P1-3): one private id ties this box to its own premium add-on line, so the cart can sit the add-on under
         its box, remove both together, and check the add-on quantity against this box's _Premium count. Underscore
         properties are hidden from the shopper in the cart and at checkout. */
      var link = "b" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      box.properties["_Box id"] = link;
      var sq = surchargeForm.querySelector("input[name='id']");
      var sfor = surchargeForm.querySelector("input[name='properties[For]']");
      var sur = { id: Number(sq.value), quantity: Math.max(1, premiumCount()), properties: { "_Box id": link } };
      if (sfor) sur.properties.For = sfor.value;
      return [box, sur];
    }
    var atomicBusy = false;
    if (addBtn) addBtn.addEventListener("click", function (e) {
      if (!(needsAtomic() || noteHasBreak()) || !(state.picks.length >= state.tier)) return;
      e.preventDefault();
      if (atomicBusy) return;
      atomicBusy = true;
      var items = atomicItems();
      var live = boxForm.querySelector("[ref='liveRegion']");
      postAddAndAnnounce(boxForm, items, { source: "ioc-box", sourceId: formId, productId: boxForm.getAttribute("data-product-id") })
        .then(function (res) {
          if (res && res.didError) { adding = false; paintBox(); if (live) live.textContent = res.message || ""; note(res.message || ""); return; }
          /* R6: when the standard-events module is unavailable the deferred CartLinesUpdateEvent never fires, so
             nothing clears the picks and none of the server-rendered cart surfaces repaint. The cart itself is always
             correct. Empty the box, then reload so every surface comes back from the server telling the truth. */
          return eventsModule().then(function (ev) {
            if (ev && ev.CartLinesUpdateEvent) return;
            state.picks = []; state.plan = ""; state.planName = ""; state.gift = { on: false, to: "", note: "" }; save();
            window.location.assign(window.location.pathname);
          });
        })
        .catch(function () { adding = false; paintBox(); })
        .then(function () { atomicBusy = false; });
    });

    // After the Box line lands (native form or the atomic path): empty the box, back to one-time (D9).
    document.addEventListener("shopify:cart:lines-update", function (e) {
      if (!boxForm || !boxForm.contains(e.target)) return;
      headFocusArmed = Date.now();
      afterCartEvent(e).then(function (res) {
        adding = false;
        if (res && res.detail && res.detail.didError) { paintBox(); return; }
        state.picks = []; state.plan = ""; state.planName = ""; state.gift = { on: false, to: "", note: "" }; pending = null; giftAsked = false;
        paintAll();
      }, function () { adding = false; paintBox(); });
    }, true);
    document.addEventListener("shopify:cart:error", function () { if (adding) { adding = false; paintBox(); } });

    /* Final-B (a11y): the add opens the cart drawer over the builder, and the button that pressed it is repainted
       (the box empties) while the drawer is up, so closing the drawer dropped focus on <body>. When the drawer was
       opened by a box add, closing it (Esc, the close button, the backdrop) puts focus on the builder heading. A drawer
       opened any other way (the header cart button) still hands focus back to its own trigger. */
    var headFocusArmed = 0, headFocusDue = false;
    document.addEventListener("theme-drawer:open", function (e) {
      if (!e.target || e.target.id !== "cart-drawer") return;
      headFocusDue = !!headFocusArmed && Date.now() - headFocusArmed < 8000;
      headFocusArmed = 0;
    });
    // "close" does not bubble; a capture listener on the document still sees it, even if a morph swaps the dialog.
    document.addEventListener("close", function (e) {
      if (!headFocusDue || !e.target || e.target.tagName !== "DIALOG" || !e.target.closest("#cart-drawer")) return;
      headFocusDue = false;
      requestAnimationFrame(function () {
        var a = document.activeElement;
        if (a && a !== document.body && !root.contains(a)) return; // the shopper already moved on (a link, the search)
        var h = root.querySelector(".bx-h1");
        if (!h) return;
        if (!h.hasAttribute("tabindex")) h.setAttribute("tabindex", "-1");
        try { h.focus({ preventScroll: true }); } catch (err) { h.focus(); }
        if (h.getBoundingClientRect().bottom < 0 || h.getBoundingClientRect().top > window.innerHeight) h.scrollIntoView({ block: "start" });
      });
    }, true);

    /* TABS fallback: until the IOC · Bundles section carries its own tab_group/tab_key settings, the packs panel is
       wired here (its root id is the anchor setting, e.g. "shop-packs"). ioc-ui.js owns the switching. */
    var tabList = root.querySelector("[data-ioc-tabs]");
    if (tabList) {
      var group = tabList.getAttribute("data-ioc-tabs");
      Array.prototype.forEach.call(tabList.querySelectorAll("[role='tab']"), function (tab) {
        var p = document.getElementById(tab.getAttribute("aria-controls") || "");
        if (!p || p.hasAttribute("data-ioc-tabpanel")) return;
        p.setAttribute("role", "tabpanel");
        p.setAttribute("aria-labelledby", tab.id);
        p.setAttribute("data-ioc-tabpanel", group);
        if (tab.getAttribute("aria-selected") !== "true") p.hidden = true;
      });
    }

    paintAll();
    if (addNotes.length) setTimeout(function () { note(addNotes[addNotes.length - 1]); }, 300);
    // ?type=gluten-friendly over a restored box with wheat picks: ask with the same confirm sheet the toggle uses.
    if (askGf) ready(function () { setTimeout(function () { if (window.IOCSheet) requestType(data.glutenTypeHandle); }, 350); });
  }

  /* ========================================================== cart gate ==== */
  function applyGate() {
    /* D2-A: while any cart surface is blocked (six minimum, or the premium add-on guard), the cart page's action bar
       ("Check out · total", a plain link to /checkout) is hidden too, so there is no way round the paused button. */
    var anyBlocked = !!document.querySelector('[data-ioc-cart-gate="blocked"]');
    document.documentElement.classList.toggle("ioc-cart-blocked", anyBlocked);
    document.querySelectorAll("[data-ioc-cart-gate]").forEach(function (marker) {
      var blocked = marker.getAttribute("data-ioc-cart-gate") === "blocked";
      var scope = marker.closest("#cart-drawer") || marker.closest(".shopify-section") || document;
      var ctas = scope.querySelectorAll(".cart__ctas");
      var btns = scope.querySelectorAll(".cart__checkout-button");
      if (!btns.length) { ctas = document.querySelectorAll(".cart__ctas"); btns = document.querySelectorAll(".cart__checkout-button"); }
      ctas.forEach(function (c) { c.classList.toggle("ioc-gated", blocked); });
      btns.forEach(function (b) {
        // only touch attributes that need to change: the observer watches disabled, so a no-op write would loop
        if (blocked) {
          if (!b.hasAttribute("disabled")) b.setAttribute("disabled", "");
          if (b.getAttribute("aria-disabled") !== "true") b.setAttribute("aria-disabled", "true");
          if (b.getAttribute("data-ioc-gated") !== "1") b.setAttribute("data-ioc-gated", "1");
          if (marker.id && b.getAttribute("aria-describedby") !== marker.id) b.setAttribute("aria-describedby", marker.id);
        } else if (b.getAttribute("data-ioc-gated") === "1") {
          b.removeAttribute("disabled"); b.removeAttribute("aria-disabled"); b.removeAttribute("data-ioc-gated"); b.removeAttribute("aria-describedby");
        }
      });
    });
  }
  var gateTimer;
  function scheduleGate() { clearTimeout(gateTimer); gateTimer = setTimeout(applyGate, 60); }
  function ready(fn) { if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", fn); else fn(); }
  ready(function () {
    applyGate();
    new MutationObserver(scheduleGate).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["disabled"] });
    document.addEventListener("shopify:cart:lines-update", function (e) { afterCartEvent(e).then(scheduleGate, scheduleGate); setTimeout(scheduleGate, 1500); });
    document.addEventListener("shopify:section:load", scheduleGate);
  });

  /* ================================ ingredients dialog (builder flavor cards) ==== */
  /* Markup is server-rendered per card by snippets/ioc-ingredients.liquid ('card' context): a [data-ioc-ingr-open]
     button and the <dialog> it controls. No button is printed when a cookie has no ingredient data, so there is never
     an empty panel to open. Native showModal(): top layer (above the dock, the toast and any banner), the page behind
     goes inert, Escape closes. We add a Tab wrap so focus never leaves the dialog, and hand focus back to the exact
     button that opened it. Document-level delegation, so it works in every builder mode and after section reloads.
     This listener never touches picking: [data-ioc-pick] lives in a separate element, and the builder's own click
     handler ignores these buttons. No live-region announcement on open (the dialog's label is read on focus). */
  var INGR_FOCUSABLE = "button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex='-1'])";
  document.addEventListener("click", function (e) {
    if (!e.target.closest) return;
    var opener = e.target.closest("[data-ioc-ingr-open]");
    if (opener) {
      var dlg = document.getElementById(opener.getAttribute("aria-controls") || "");
      if (!dlg || dlg.open) return;
      e.preventDefault();
      dlg.__iocOpener = opener;
      if (typeof dlg.showModal === "function") dlg.showModal(); else dlg.setAttribute("open", "");
      var first = dlg.querySelector("[data-ioc-ingr-close]");
      if (first) first.focus();
      return;
    }
    var closer = e.target.closest("[data-ioc-ingr-close]");
    if (closer) {
      var d = closer.closest("[data-ioc-ingr-dialog]");
      if (d && d.open) { if (typeof d.close === "function") d.close(); else { d.removeAttribute("open"); restoreIngrFocus(d); } }
      return;
    }
    // Backdrop click (outside the dialog box) closes it. Keyboard-synthesised clicks never land on the dialog itself.
    if (e.target.matches && e.target.matches("[data-ioc-ingr-dialog][open]")) {
      var r = e.target.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) e.target.close();
    }
  });
  function restoreIngrFocus(dlg) {
    var o = dlg.__iocOpener;
    dlg.__iocOpener = null;
    if (o && document.contains(o)) o.focus();
  }
  // "close" does not bubble; capture catches it for every ingredients dialog, however it was closed (button, Escape).
  document.addEventListener("close", function (e) {
    if (e.target && e.target.matches && e.target.matches("[data-ioc-ingr-dialog]")) restoreIngrFocus(e.target);
  }, true);
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Tab") return;
    var dlg = document.querySelector("[data-ioc-ingr-dialog][open]");
    if (!dlg) return;
    var items = Array.prototype.filter.call(dlg.querySelectorAll(INGR_FOCUSABLE), function (el) { return el.getClientRects().length; });
    if (!items.length) return;
    var firstEl = items[0], lastEl = items[items.length - 1];
    if (!dlg.contains(document.activeElement)) { e.preventDefault(); firstEl.focus(); return; }
    if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
    else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
  });
})();
/* D2-A (WCAG 2.4.11): scroll-padding keeps a focused control clear of the fixed action bar wherever the page can
   scroll it clear. A control that cannot scroll (the desktop sticky gallery's thumbnails) and lands in the bar's strip
   makes the bar step aside (html.ioc-abar-yield, assets/ioc-shop.css.liquid) until focus moves on. Keyboard focus
   only (:focus-visible); a mouse click never hides the bar. */
(function () {
  var root = document.documentElement;
  var cur = null, raf = 0;
  function bar() { return document.querySelector("[data-ioc-abar]"); }
  function kbd(el) { try { return el.matches(":focus-visible"); } catch (e) { return false; } }
  function check() {
    raf = 0;
    var b = bar(), on = false;
    if (b && cur && document.contains(cur) && !b.contains(cur) && kbd(cur)) {
      var h = b.offsetHeight, r = cur.getBoundingClientRect();
      if (h && r.height && r.bottom > window.innerHeight - h && r.top < window.innerHeight) on = true;
    }
    root.classList.toggle("ioc-abar-yield", on);
  }
  function soon() { if (!raf) raf = requestAnimationFrame(check); }
  document.addEventListener("focusin", function (e) {
    cur = e.target && e.target !== document.body ? e.target : null;
    soon();
    setTimeout(check, 400); // after any smooth focus scroll settles
  });
  document.addEventListener("focusout", function () {
    setTimeout(function () {
      var a = document.activeElement;
      if (!a || a === document.body) { cur = null; root.classList.remove("ioc-abar-yield"); }
    }, 0);
  });
  document.addEventListener("scroll", function () { if (cur) soon(); }, { capture: true, passive: true });
  window.addEventListener("resize", function () { if (cur) soon(); });
})();
/* D2-A (WCAG 2.4.11): Horizon's quantity inputs select their value on focus (component-quantity-selector.js). In
   Safari/WebKit that select() cancels the browser's own scroll-to-focus, so Tab can land on a quantity box below the
   fold (Box PDP). If the focused box is out of view one frame later, bring it in. A no-op where the browser already
   scrolled it (Chromium, Firefox). */
(function () {
  document.addEventListener("focusin", function (e) {
    var t = e.target;
    if (!(t instanceof HTMLInputElement) || !t.closest("quantity-selector-component, cart-quantity-selector-component")) return;
    requestAnimationFrame(function () {
      if (document.activeElement !== t) return;
      var r = t.getBoundingClientRect();
      if (r.height && (r.bottom > window.innerHeight || r.top < 0)) t.scrollIntoView({ block: "nearest" });
    });
  });
})();
